import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, RotateCcw, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  title: "Title",
  tagline: "Tagline",
  executive_summary: "Executive summary",
  creative_direction: "Creative direction",
  moodboard: "Moodboard",
  production_type: "Format & scope",
  deliverables: "Deliverables",
  budget: "Budget",
  timeline: "Timeline",
  casting: "Casting",
  locations: "Locations",
  equipment: "Equipment",
  crew: "Crew",
  next_steps: "Next steps",
};

const labelFor = (key) => FIELD_LABELS[key] || key;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function HistoryModal({ blueprintId, onClose, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blueprint/${blueprintId}/revisions`);
        setItems(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [blueprintId]);

  const restore = async (revId) => {
    if (restoring) return;
    setRestoring(revId);
    try {
      const { data } = await axios.post(
        `${API}/blueprint/${blueprintId}/revisions/${revId}/restore`,
        {}
      );
      onRestored?.(data);
    } catch (e) {
      console.error(e);
      setRestoring(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-white flex items-start justify-center px-6 py-16 overflow-y-auto"
      data-testid="history-modal"
    >
      <div className="w-full max-w-[720px]">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="eyebrow eyebrow-denim mb-3">History</div>
            <h2 className="font-display font-extrabold text-[30px] md:text-[42px] tracking-[-0.035em] leading-[1.05] text-ink">
              Every <span className="font-serif-italic font-light">version</span>, remembered.
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="w-10 h-10 rounded-full border border-bnm flex items-center justify-center hover:border-black"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && <div className="text-muted-ink text-sm">Loading revisions…</div>}
        {!loading && items.length === 0 && (
          <div className="bnm-card p-6">
            <p className="text-muted-ink text-[14.5px] leading-[1.65]">
              No revisions yet. Ask your Producer to change something and it&rsquo;ll show up here.
            </p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <ol className="space-y-3">
            {items.map((r) => (
              <li
                key={r.id}
                className="bnm-card p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`history-item-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="eyebrow eyebrow-muted mb-1.5">{formatDate(r.created_at)}</div>
                  <div className="font-display font-semibold text-[16px] md:text-[17px] tracking-[-0.01em] text-ink leading-[1.3]">
                    &ldquo;{r.instruction}&rdquo;
                  </div>
                  {r.changed_fields?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {r.changed_fields.map((f) => (
                        <span
                          key={f}
                          className="text-[11px] eyebrow eyebrow-muted border border-bnm rounded-full px-2.5 py-1"
                        >
                          {labelFor(f)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => restore(r.id)}
                  disabled={restoring !== null}
                  className="pill inline-flex items-center gap-2 shrink-0 disabled:opacity-50"
                  data-testid={`history-restore-${r.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>{restoring === r.id ? "Restoring…" : "Restore this"}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-ink" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
